import React from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { characters, upcoming } from "@/data/characters";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09 } },
};
const item = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export default function Characters() {
  return (
    <section
      id="bohaterowie"
      className="relative py-28 md:py-40 overflow-hidden"
      data-testid="characters-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9 }}
          className="mb-16 md:mb-24 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <div>
            <span className="chip" data-testid="characters-eyebrow">
              <span className="dot" /> Bohaterowie
            </span>
            <h2 className="font-display font-semibold text-[#051937] text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.02] tracking-tight max-w-3xl">
              Siedmioro serc, jedna banda, <em className="not-italic text-[#60A5FA]">tysiąc pytań.</em>
            </h2>
          </div>
          <p className="text-lg text-[#0b2a55]/75 max-w-sm">
            Każdy bohater ma swój charakter, swoją słabość i swoją supermoc. Razem tworzą świat,
            do którego dziecko chce wracać wieczorami.
          </p>
        </motion.div>

        <motion.ul
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8"
        >
          {characters.map((c) => (
            <motion.li
              key={c.slug}
              variants={item}
              className="group relative rounded-[2rem] bg-white border border-[#BAE6FD]/70 overflow-hidden hover:-translate-y-2 transition-transform duration-500 shadow-[0_8px_30px_-10px_rgba(11,42,85,0.15)]"
              data-testid={`char-${c.slug}`}
            >
              <div
                className="relative aspect-[3/4] w-full overflow-hidden"
                style={{ background: `linear-gradient(180deg, ${c.color}22 0%, ${c.color}55 100%)` }}
              >
                <motion.img
                  src={c.portrait}
                  alt={c.name}
                  className="absolute inset-0 w-full h-full object-contain object-bottom p-4"
                  whileHover={{ scale: 1.06, rotate: [-1, 1, -1, 0] }}
                  transition={{ duration: 0.6 }}
                />
              </div>
              <div className="p-5 md:p-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-semibold text-2xl text-[#051937]">{c.name}</h3>
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ background: c.color }}
                    aria-hidden="true"
                  />
                </div>
                <p className="mt-1 text-xs uppercase tracking-[0.18em] font-bold text-[#0b2a55]/60">
                  {c.role}
                </p>
                <p className="mt-3 text-[#0b2a55]/80 leading-relaxed text-sm">{c.tagline}</p>
              </div>
            </motion.li>
          ))}
        </motion.ul>

        {/* Coming soon rail */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9 }}
          className="mt-16 md:mt-24 rounded-[2rem] bg-[#E0F2FE]/60 border border-[#BAE6FD]/70 p-8 md:p-12"
          data-testid="coming-soon"
        >
          <div className="flex items-center gap-3 text-[#051937]">
            <Sparkles size={22} />
            <span className="font-display font-semibold text-2xl md:text-3xl">
              Kolejni bohaterowie już wkrótce
            </span>
          </div>
          <ul className="mt-6 flex flex-wrap gap-3">
            {upcoming.map((name) => (
              <li
                key={name}
                className="chip !bg-white !text-[#051937] !border-[#BAE6FD]"
                data-testid={`upcoming-${name}`}
              >
                {name}
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}
